import {Component, Input} from '@angular/core';
import {OpenAPIService} from '../services/openapi';
import { PlatformService } from '../services/platform.service';

declare let window:any;

const getType = (schema) => {
  if (Array.isArray(schema.type)) return schema.type.join(', ');
  if (schema.type) return schema.type;
  if (schema.properties) return 'object';
  if (schema.items) return 'array';
  return 'any';
}

@Component({
  selector: 'schema',
  templateUrl: '../views/schema.pug',
  styles: [`
    subschema {
      display: block;
    }
  `],
})
export class SchemaComponent {
  @Input() title;
  @Input() schema:any;
  @Input() examples;
  @Input() depth:number = 3;

  showing:string = 'schema';
  highlightedExample:string;

  constructor(
    private openapi:OpenAPIService,
    private platformService:PlatformService
  ) {}

  ngOnChanges() {
    let example = this.schema.example;
    if (!example) {
      if (this.examples && Object.keys(this.examples).length) {
        let keys = Object.keys(this.examples);
        // FIXME: have a dropdown for different content types.
        if (keys.indexOf('application/json') !== -1) {
          example = this.examples['application/json'];
        } else {
          example = this.examples[keys[0]];
        }
      } else {
        example = this.getExample(this.schema);
      }
    }
    let exampleStr = JSON.stringify(example, null, 2) || '';

    if (window.Prism !== null) {
      this.highlightedExample = window.Prism.highlight(exampleStr, window.Prism.languages.json);
    }
  }

  getType(schema) {
    if (schema.$ref) {
      schema = this.openapi.resolveReference(schema.$ref);
    }
    return getType(schema);
  }

  getExample(schema, depth=3) {
    if (depth < 0) return;
    if (!schema) return null;
    if (schema.$ref) schema = this.openapi.resolveReference(schema.$ref);
    let type = Array.isArray(schema.types) ? schema.types[0] : this.getType(schema);
    if (type === 'any') {
      return {};
    } else if (type === 'object') {
      let example = {};
      let extensions = [];
      if (schema.allOf) extensions = extensions.concat(schema.allOf);
      // TODO: allow anyOf/oneOf without hitting circular issues
      //if (schema.anyOf) extensions.push(schema.anyOf[0]);
      //if (schema.oneOf) extensions.push(schema.oneOf[0]);
      extensions = extensions.map(e => this.getExample(e, depth));
      extensions.forEach(ext => {
        for (let key in ext) {
          example[key] = ext[key];
        }
      })
      for (let key in schema.properties) {
        example[key] = this.getExample(schema.properties[key] || {}, depth - 1);
      }
      return example;
    } else if (type === 'array') {
      return [this.getExample(schema.items || {}, depth - 1)]
    } else {
      if (schema.default) return schema.default;
      if (schema.enum) return schema.enum[0];
      if (type === 'string') {
        return "";
      } else if (type === 'number') {
        return 0.0;
      } else if (type === 'integer') {
        return 0;
      } else if (type === 'boolean') {
        return true;
      }
    }
  }
}

@Component({
    selector: 'subschema',
    templateUrl: '../views/subschema.pug',
    styles: [`
      subschema {
        display: block;
        margin-left: 10px;
      }
      .title-line, .description {
        white-space: nowrap;
      }
      .enum {
        word-break: break-word;
      }
      .sibling-group > schema {
        margin-left: 0px;
      }
      .sibling-group h5 {
        font-weight: normal;
        font-style: italic;
      }
      .enum, .description {
        padding-left: 10px;
      }
      :host >>> subschema .description p {
        margin-bottom: 0px;
        display: inline-block;
      }
    `]
})
export class SubschemaComponent {
  @Input('title') inputTitle;
  @Input() name;
  @Input() schema:any;
  @Input() depth:number = 0;
  @Input() expanded:boolean;
  @Input() hideTitleLine:boolean;
  @Input() required:boolean = false;
  title:string;
  type:string;
  description:string;
  properties:any[] = [];

  marked:Function = window.marked;

  constructor(private openapi:OpenAPIService) {
  }

  ngOnInit() {
    this.schema = this.schema || {};
    if (this.expanded === undefined) {
      this.expanded = this.depth > 0;
    }
    this.maybeResolveRef();
  }

  ngOnChanges() {
    this.reset();
    this.maybeResolveRef();
    if (this.hideTitleLine) {
      this.expanded = true;
    }
  }

  reset() {
    this.type = this.description = this.properties = null;
    this.title = this.inputTitle;
  }

  maybeResolveRef() {
    if (this.schema.$ref) {
      this.title = this.getTitle(this.schema);
      this.setSchema(this.openapi.resolveReference(this.schema.$ref));
    } else {
      this.setSchema(this.schema);
    }
  }

  setSchema(schema) {
    this.schema = schema || {};
    if (this.schema.title) this.title = this.schema.title;
    this.type = this.getType(this.schema);
    this.description = this.schema.description;
    if (this.isPrimitive()) {
      this.expanded = true;
    }
    if (this.schema.properties) {
      this.properties = Object.keys(this.schema.properties);
      if (this.schema.required) {
        this.properties.sort((p1, p2) => {
          let r1 = this.schema.required.indexOf(p1) + 1;
          let r2 = this.schema.required.indexOf(p2) + 1;
          if (r1 && !r2) return -1;
          if (r2 && !r1) return 1;
          return 0;
        });
      }
    }
  }

  isPrimitive() {
    return !this.schema.$ref && this.type !== 'array' && this.type !== 'object';
  }

  getTitle(schema) {
    let ref = schema.$ref;
    if (ref) {
      schema = this.openapi.resolveReference(ref);
    }
    if (schema.title) return schema.title;
    if (!ref) return '';
    let title = ref;
    let slash = title.lastIndexOf('/');
    if (slash >= 0) {
      title = title.substring(slash + 1);
    }
    return title;
  }

  getType(schema) {
    if (schema.$ref) {
      schema = this.openapi.resolveReference(schema.$ref);
    }
    return getType(schema);
  }
}



// WEBPACK FOOTER //
// ./src/app/components/schema.ts